import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormElement, Form, FormClassComponent, Field, FormRenderProps, FormHandle } from '@progress/kendo-react-form'
import { GridCellProps, GridRowClickEvent } from '@progress/kendo-react-grid'
import { Label } from '@progress/kendo-react-labels'
import { FileUpload } from 'components'
import DefaultButton from 'components/Buttons/DefaultButton/DefaultButton'
import FormInput from 'components/Form/FormInput'
import SAGrid, { GetDataResponse, GridGetDataProps, ISAGridRef } from 'components/Grid/SAGrid'
import ConfirmModal from 'components/Modals/ConfirmModal'
import Spinner from 'components/Spinner/Spinner'
import { permissionMap } from 'core/helpers'
import { V1ApiSharedClient } from 'core/http-common'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { ICustomColumn, IGridLocalStorageNames } from 'types'
import { toast } from 'core/toast'
import { ALLOWED_EXCEL_EXTENSIONS, ALLOWED_IMAGE_EXTENSIONS, ALLOWED_PDF_EXTENSIONS, ALLOWED_TEXT_EXTENSIONS, MAX_FILE_SIZE } from 'core/consts'
import { AttachmentDto, AttachmentTypeEnum, CreateAttachmentDto, UpdateAttachmentDescriptionDto } from 'generated/v1/v1SharedApi'
import { hasPermission } from 'state/reduxHelpers'
interface IProps {
    attachmentTypeId?: number
    attachmentType: AttachmentTypeEnum
    editable: boolean
    formIsModified: boolean

}

const LOCAL_STORAGE_KEYS = {
    CASES: {
        SORT: 'caseAttachmentsSort',
        SEARCH: 'caseAttachmentsSearch',
        PAGE_SIZE: 'caseAttachmentsPageSize',
        SELECTED_PAGE: 'caseAttachmentsSelectedPage'
    },
    DEALERS: {
        SORT: 'dealerAttachmentsSort',
        SEARCH: 'dealerAttachmentsSearch',
        PAGE_SIZE: 'dealerAttachmentsPageSize',
        SELECTED_PAGE: 'dealerAttachmentsSelectedPage'
    }
}

const AttachmentsForm = ({ editable, attachmentTypeId, attachmentType, formIsModified }: IProps) => {
    // local storage 
    const [gridLocalStorageNames, setGridLocalStorageNames] = useState<IGridLocalStorageNames>()

    //add attachment
    const [showAddModal, setShowAddModal] = useState(false)
    const [showPreviewModal, setShowPreviewModal] = useState(false)
    const addAttachmentFormRef = useRef<FormClassComponent>(null)
    const [saveAttachmentLoading, setSaveAttachmentLoading] = useState(false)

    //file 
    const [fileUploadKey, setFileUploadKey] = useState(1)
    const [fileUrl, setFileUrl] = useState<string | null>(null)
    const [file, setFile] = useState<File | undefined>(undefined)
    const [fetchingFileUrl, setFetchingFileUrl] = useState(false)
    const [selectedAttachment, setSelectedAttachment] = useState<AttachmentDto | null>(null)


    //edit attachment
    const [showEditModal, setShowEditModal] = useState(false)
    const [editAttachmentLoading, setEditAttachmentLoading] = useState(false)
    const [currentDescription, setCurrentDescription] = useState<string | undefined>(undefined)
    const editAttachmentFormRef = useRef<FormClassComponent>(null)

    // refs 
    const formPreviewModal = useRef<FormHandle>(null)
    const gridRef = useRef<ISAGridRef>(null)


    const checkPermission = (type: keyof typeof permissionMap, action: 'add' | 'edit') => {
        const permission = permissionMap[type]?.[action]
        const ispermission = permission ? hasPermission(permission) : false
        return ispermission
    }

    //use memo to memoize the permission check
    const [canEditAttachment, canAddAttachment] = useMemo(() => {
        return [
            checkPermission(attachmentType, 'edit'),
            checkPermission(attachmentType, 'add'),
        ]
    }, [attachmentType])

    const getData = async (props: GridGetDataProps): Promise<GetDataResponse> => {
        try {
            if (attachmentTypeId && attachmentType) {
                const result = await V1ApiSharedClient.getAttachments(
                    attachmentTypeId,
                    attachmentType,
                    props.search,
                    props.sortBy,
                    props.sortAscending,
                    props.paging && props.paging.currentPage ? props.paging.currentPage : 1,
                    props.paging ? props.paging.take : 10)

                return {
                    data: result.data,
                    total: result.totalItems ? result.totalItems : 0
                }
            }
        }
        catch (e) {
            toast.responseErrors(e)
        }
        return {
            data: [],
            total: 0
        }
    }

    const handleAddAttachment = async () => {
        try {
            setSaveAttachmentLoading(true)
            if (!file) {
                toast.error('Please select a file to upload.')
                return
            }
            // Example: Submit the formData to an API endpoint
            const response = await V1ApiSharedClient.saveAttachment({ data: file, fileName: file ? file.name : '' })

            if (response.success && response.payload && attachmentTypeId) {
                const newAttachment = new CreateAttachmentDto({
                    id: attachmentTypeId,
                    attachmentType: attachmentType,
                    name: file.name,
                    path: response.payload.fileName,
                    description: addAttachmentFormRef.current?.valueGetter('fileDescription') as string,
                })
                const res = await V1ApiSharedClient.addAttachment(newAttachment as CreateAttachmentDto)
                if (res && res.success) {
                    toast.success('Attachment added successfully!')
                    setFile(undefined) // Reset the file state
                    setFileUploadKey((prev) => prev + 1) // Reset the file upload component by changing the key
                    setShowAddModal(false) // Close the modal

                }
            }
        } catch (error) {
            toast.error('Failed to upload file. Please try again.')
        }
        finally {
            setSaveAttachmentLoading(false)
        }
    }

    const fetchFile = async (fileName: string) => {
        try {
            setFetchingFileUrl(true)
            const response = await V1ApiSharedClient.getAttachment(fileName)
            const blob = response.data
            const url = URL.createObjectURL(blob)
            setFileUrl(url)
        } catch (error) {
            toast.error('Failed to fetch file. Please try again.')
            setFileUrl(null)
        }
        finally {
            setFetchingFileUrl(false)
        }
    }

    const handleEditAttachment = async () => {
        try {
            setEditAttachmentLoading(true)
            if (!selectedAttachment) {
                toast.error('Please select an attachment to edit. ')
                return
            }

            const response = await V1ApiSharedClient.updateAttachmentDescription(new UpdateAttachmentDescriptionDto({
                attachmentId: selectedAttachment.attachmentId,
                description: editAttachmentFormRef.current?.valueGetter('fileDescription') as string,
                attachmentType: attachmentType,
            }))

            if (response && response.success) {
                toast.success('Attachment updated successfully!')
                setFile(undefined) // Reset the file state
                setShowEditModal(false) // Close the modal

            }
        }
        catch (error) {
            toast.error('Failed to update attachment. Please try again.')
        }
        finally {
            setEditAttachmentLoading(false)
        }
    }


    const handleViewCellClick = (attachment: AttachmentDto) => {
        fetchFile(attachment.path ? attachment.path : '')
        setSelectedAttachment(attachment)
        setShowPreviewModal(true)
    }
    const handleRowClick = (attachment: AttachmentDto) => {
        if (!canEditAttachment) {
            toast.error('You do not have permission to edit this attachment.')
            return
        }
        setCurrentDescription(attachment.description)
        setSelectedAttachment(attach => {
            setShowEditModal(true)
            return attachment
        })
    }

    const handleLocalStorageNames = () => {
        switch (attachmentType) {
            case AttachmentTypeEnum.Case:
                setGridLocalStorageNames({
                    sort: LOCAL_STORAGE_KEYS.CASES.SORT,
                    search: LOCAL_STORAGE_KEYS.CASES.SEARCH,
                    selectedPage: LOCAL_STORAGE_KEYS.CASES.SELECTED_PAGE,
                    pageSize: LOCAL_STORAGE_KEYS.CASES.PAGE_SIZE
                })
                break
            case AttachmentTypeEnum.Dealer:
                setGridLocalStorageNames({
                    sort: LOCAL_STORAGE_KEYS.DEALERS.SORT,
                    search: LOCAL_STORAGE_KEYS.DEALERS.SEARCH,
                    selectedPage: LOCAL_STORAGE_KEYS.DEALERS.SELECTED_PAGE,
                    pageSize: LOCAL_STORAGE_KEYS.DEALERS.PAGE_SIZE
                })
                break
        }

    }

    const handleAddModalClose = () => {
        setFile(undefined) // Reset the file state
        setShowAddModal(false) // Close the modal
    }


    useEffect(() => {
        if (attachmentTypeId && attachmentType) {
            handleLocalStorageNames()
        }
    }, [attachmentType, attachmentTypeId])




    const viewCell = (props: GridCellProps) => {

        return (
            <td className='text-center'>
                <DefaultButton variant='default' className='btn-sm' onClick={() => (handleViewCellClick(props.dataItem as AttachmentDto))}><FontAwesomeIcon icon={['fas', 'search']} /></DefaultButton>
            </td>
        )
    }

    const attachmentColumns: ICustomColumn[] = [
        { field: 'view', title: 'View', sortable: false, width: '80px', cell: viewCell },
        { field: 'name', title: 'File Name', },
        { field: 'description', title: 'Description' },
        { field: 'createdBy', title: 'Created By' },
        { field: 'createdDate', title: 'Created Date', format: '{0:d}' },
        { field: 'lastModifiedBy', title: 'Last Modified By' },
        { field: 'lastModifiedDate', title: 'Last Modified Date', format: '{0:d}' },
    ]

    return (
        <div id='AttachmentsForm'>

            <div className='bg-light shadow-sm p-3 border pb-5'>
                <Row>
                    <Col md={6}><h4>{attachmentType} Attachments</h4></Col>
                    <Col md={6} className='text-start text-md-end' style={{ alignSelf: 'center' }}>
                        <DefaultButton
                            className='btn-sm'
                            variant='default'
                            onClick={() => (setShowAddModal(true))}
                            disabled={!canAddAttachment}
                        >
                            <FontAwesomeIcon icon={['fas', 'plus-circle']} /> {' '}
                            Add Attachment
                        </DefaultButton>
                    </Col>
                </Row>
                <hr />
                <Row>
                    {gridLocalStorageNames &&
                        <Col xs={12}>
                            <SAGrid
                                ref={gridRef}
                                features={
                                    {
                                        pageable: true,
                                        reorderColumns: false,
                                        simpleSearch: true,
                                        sortable: true,
                                    }
                                }
                                columns={attachmentColumns}
                                defaultSort={[{ field: 'lastModifiedDate', dir: 'desc' }]}
                                getData={getData}
                                onRowClick={(e: GridRowClickEvent) => handleRowClick(e.dataItem as AttachmentDto)}
                                localStorageNames={gridLocalStorageNames}
                            />
                        </Col>
                    }

                </Row>
            </div>

            <ConfirmModal
                show={showAddModal}
                title='Add Attachment'
                confirmButtonTitle='Add Attachment'
                onCancel={handleAddModalClose}
                disabled={!file}
                loading={saveAttachmentLoading}
                onSave={handleAddAttachment}
                content={
                    <Form
                        ignoreModified={true}
                        ref={addAttachmentFormRef}
                        render={() => (
                            <FormElement>
                                <Row>
                                    <Col md={12} className='mb-3'>
                                        <Field
                                            component={FormInput}
                                            name='fileDescription'
                                            label='File Description:'
                                        />
                                    </Col>
                                    <Col md={12}>
                                        <Label>Upload File:</Label>
                                        <FileUpload
                                            setFile={setFile}
                                            lable=''
                                            showPreview={false}
                                            key={fileUploadKey}
                                            allowedExtensions={[...ALLOWED_PDF_EXTENSIONS, ...ALLOWED_EXCEL_EXTENSIONS, ...ALLOWED_IMAGE_EXTENSIONS, ...ALLOWED_TEXT_EXTENSIONS]}
                                            maxFileSize={MAX_FILE_SIZE}
                                            accept={[...ALLOWED_PDF_EXTENSIONS, ...ALLOWED_EXCEL_EXTENSIONS, ...ALLOWED_IMAGE_EXTENSIONS, ...ALLOWED_TEXT_EXTENSIONS]}
                                        />
                                    </Col>
                                </Row>

                            </FormElement>
                        )}
                    />
                }
            />

            <ConfirmModal className='modal-lg'
                show={showPreviewModal}
                title='Preview Attachment'
                confirmButtonTitle='Save'
                onCancel={() => setShowPreviewModal(false)}
                onSave={() => setShowPreviewModal(false)}
                showCancelButton={false}
                showConfirmButton={false}
                content={
                    fetchingFileUrl ? (
                        <Spinner center />
                    ) :
                        <Form
                            ignoreModified={true}
                            ref={formPreviewModal}
                            render={() => (
                                <FormElement>
                                    <Row>
                                        <Col md={12} className='mb-3 d-flex justify-content-center'>
                                            {selectedAttachment ? (
                                                <>
                                                    {selectedAttachment.path?.toLowerCase().endsWith('.pdf') ? (
                                                        // PDF Viewer
                                                        <iframe
                                                            src={fileUrl || ''}
                                                            title='PDF Preview'
                                                            width='100%'
                                                            height='500px'
                                                        />
                                                    ) : selectedAttachment?.path?.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/i) ? (
                                                        // Image Viewer
                                                        <img
                                                            src={fileUrl || ''}
                                                            alt={selectedAttachment?.name}
                                                            style={{ maxWidth: '100%', maxHeight: '500px' }}
                                                        />
                                                    ) : selectedAttachment?.path?.toLowerCase().endsWith('.txt') ? (
                                                        // Text Viewer
                                                        <iframe
                                                            src={fileUrl || ''}
                                                            title='Text Preview'
                                                            width='100%'
                                                            height='500px'
                                                        />
                                                    ) : (
                                                        // Fallback for unsupported file types
                                                        <p>
                                                            Unable to preview this file type.{' '}
                                                            <a href={fileUrl || ''} target='_blank' rel='noopener noreferrer'>
                                                                Download the file
                                                            </a>
                                                        </p>
                                                    )}
                                                </>
                                            ) : (
                                                <p>No attachment selected for preview.</p>
                                            )}
                                        </Col>
                                    </Row>
                                </FormElement>
                            )}
                        />
                }
            />
            <ConfirmModal
                show={showEditModal}
                title='Update Attachment'
                confirmButtonTitle='Update'
                onCancel={() => setShowEditModal(false)}
                loading={editAttachmentLoading}
                onSave={handleEditAttachment}
                disabled={selectedAttachment?.description?.trim() === currentDescription?.trim()}
                content={
                    <Form
                        ignoreModified={true}
                        ref={editAttachmentFormRef}
                        initialValues={{
                            fileDescription: selectedAttachment?.description || '', // Provide a fallback value
                        }}
                        render={() => (
                            <FormElement>
                                <Row>
                                    <Col md={12} className='mb-3'>
                                        <Field
                                            component={FormInput}
                                            name='fileDescription'
                                            label='File Description:'
                                            onChange={(e) => { setCurrentDescription(e.target.value) }}
                                        />
                                    </Col>
                                </Row>

                            </FormElement>
                        )}
                    />
                }
            />

        </div>
    )
}
export default AttachmentsForm

